import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BookOpen, Users } from 'lucide-react';
import { ATOMOS_CHAT_GUIDE_IMAGES } from '@/constants/atomosChatGuideImages';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';

const CHAT_ATOMOS_URL = 'https://chat.atomos.tech/';

export interface AtomosChatGuideForm {
  account_token: string;
  account_name: string;
  channel_name: string;
  channel_phone: string;
  channel_id: string;
  auto_close_enabled: boolean;
  auto_close_delay_minutes: number;
  auto_close_unit: 'minutes' | 'hours';
}

interface SavedTeam {
  team_name: string;
  team_id: string;
}

interface DepartmentDto {
  id: string;
  name: string;
}

interface AtomosChatApiGuideProps {
  form: AtomosChatGuideForm;
  onFormChange: (field: keyof AtomosChatGuideForm, value: string | boolean | number) => void;
  onSave: () => void;
  saving: boolean;
  error?: string;
  mode?: 'create' | 'edit';
  onCancel?: () => void;
  clientId: number;
  editingConfigId?: number | null;
}

export default function AtomosChatApiGuide({
  form,
  onFormChange,
  onSave,
  saving,
  error,
  mode = 'create',
  onCancel,
  clientId,
  editingConfigId,
}: AtomosChatApiGuideProps) {
  const isEdit = mode === 'edit';
  const { toast } = useToast();
  const [teams, setTeams] = useState<SavedTeam[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const autoFetchedRef = useRef<number | null>(null);

  const loadAndSaveEquipes = async () => {
    if (!clientId || !editingConfigId) return;
    try {
      setLoadingTeams(true);
      const depRes = await apiService.get(
        `/atomos-chat/client/${clientId}/departments?config_id=${editingConfigId}`
      ) as { success: boolean; departments: DepartmentDto[] };
      const departments = depRes?.departments ?? [];
      const merged: SavedTeam[] = departments.map((d) => ({
        team_name: d.name,
        team_id: d.id,
      }));
      await apiService.put(`/atomos-chat/client/${clientId}/saved-teams`, {
        atomos_chat_config_id: editingConfigId,
        teams: merged,
      });
      setTeams(merged);
      toast({
        title: 'Equipes carregadas',
        description: `${merged.length} equipe(s) salvas para esta conexão.`,
      });
    } catch (e: any) {
      console.error('[AtomosChatApiGuide] loadAndSaveEquipes:', e);
      toast({
        title: 'Erro ao carregar equipes',
        description: e?.message || 'Erro interno',
        variant: 'destructive',
      });
    } finally {
      setLoadingTeams(false);
    }
  };

  useEffect(() => {
    if (isEdit && editingConfigId && autoFetchedRef.current !== editingConfigId) {
      autoFetchedRef.current = editingConfigId;
      loadAndSaveEquipes();
    }
    if (!isEdit) {
      setTeams([]);
      autoFetchedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editingConfigId]);

  return (
    <Card className="border-2 border-teal-200 bg-teal-50/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-teal-800">
          <BookOpen className="w-5 h-5" />
          {isEdit ? 'Editar conexão (Envios V2 - ÁtomosChat)' : 'Configure a integração (Envios V2 - ÁtomosChat)'}
        </CardTitle>
        <CardDescription className="text-teal-600">
          {isEdit
            ? 'Atualize os dados desta conexão. Deixe o token em branco para mantê-lo.'
            : 'Siga o passo a passo e preencha cada campo no próprio passo'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>
        )}
        <Accordion type="single" collapsible className="w-full" defaultValue="step1">
          <AccordionItem value="step1">
            <AccordionTrigger>1. Token da conta (Bearer)</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <p className="text-gray-700">Onde localizar essa informação?</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                <li>
                  Acesse:{' '}
                  <a href={CHAT_ATOMOS_URL} target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">
                    {CHAT_ATOMOS_URL}
                  </a>
                </li>
                <li>Na conta do cliente: <strong>Ajustes → Integrações</strong></li>
                <li><strong>Integração via API → Configurar</strong> → <strong>Novo</strong>, nome como <strong>atomosChat</strong></li>
                <li>Clique no ícone de copiar e cole aqui:</li>
              </ul>
              {ATOMOS_CHAT_GUIDE_IMAGES.step1 && (
                <img src={ATOMOS_CHAT_GUIDE_IMAGES.step1} alt="Menu Ajustes e Integrações" className="rounded border max-w-full h-auto" />
              )}
              {ATOMOS_CHAT_GUIDE_IMAGES.step2 && (
                <img src={ATOMOS_CHAT_GUIDE_IMAGES.step2} alt="Integração via API" className="rounded border max-w-full h-auto" />
              )}
              {ATOMOS_CHAT_GUIDE_IMAGES.step3 && (
                <img src={ATOMOS_CHAT_GUIDE_IMAGES.step3} alt="Copiar token" className="rounded border max-w-full h-auto" />
              )}
              <div className="pt-2">
                <Label htmlFor="atomos-account_token">Cole o token aqui</Label>
                <Input
                  id="atomos-account_token"
                  type="password"
                  placeholder="Deixe em branco para manter o atual"
                  value={form.account_token}
                  onChange={(e) => onFormChange('account_token', e.target.value)}
                  className="mt-1 max-w-md"
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="step2">
            <AccordionTrigger>2. Nome do cliente na plataforma</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <p className="text-gray-700 text-sm">
                Use o mesmo nome da conta na plataforma (chat.atomos.tech).
              </p>
              <div>
                <Label htmlFor="atomos-account_name">Nome do cliente</Label>
                <Input
                  id="atomos-account_name"
                  value={form.account_name}
                  onChange={(e) => onFormChange('account_name', e.target.value)}
                  placeholder="Ex: Minha Empresa"
                  className="mt-1 max-w-md"
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="step3">
            <AccordionTrigger>3. Nome do canal</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <p className="text-gray-700 text-sm">
                Use como utilidade da conexão: <strong>Central Administrativa</strong>, <strong>Financeiro</strong>, <strong>Assistência 24h</strong>, etc.
              </p>
              <div>
                <Label htmlFor="atomos-channel_name">Nome do canal</Label>
                <Input
                  id="atomos-channel_name"
                  value={form.channel_name}
                  onChange={(e) => onFormChange('channel_name', e.target.value)}
                  placeholder="Ex: Central Administrativa"
                  className="mt-1 max-w-md"
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="step4">
            <AccordionTrigger>4. Telefone do canal e Channel ID</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                <li><strong>Ajustes → Conta → Canais de atendimento</strong> → clique no canal</li>
                <li><strong>Número:</strong> copie só os dígitos (sem (), - ou espaços)</li>
                <li><strong>Channel ID:</strong> copie o Id na mesma tela e cole abaixo</li>
              </ul>
              {ATOMOS_CHAT_GUIDE_IMAGES.step4 && (
                <img src={ATOMOS_CHAT_GUIDE_IMAGES.step4} alt="Canais" className="rounded border max-w-full h-auto" />
              )}
              {ATOMOS_CHAT_GUIDE_IMAGES.step5 && (
                <img src={ATOMOS_CHAT_GUIDE_IMAGES.step5} alt="Id e Número" className="rounded border max-w-full h-auto" />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div>
                  <Label htmlFor="atomos-channel_phone">Telefone do canal</Label>
                  <Input
                    id="atomos-channel_phone"
                    value={form.channel_phone}
                    onChange={(e) => onFormChange('channel_phone', e.target.value)}
                    placeholder="82993423134"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="atomos-channel_id">Channel ID</Label>
                  <Input
                    id="atomos-channel_id"
                    value={form.channel_id}
                    onChange={(e) => onFormChange('channel_id', e.target.value)}
                    placeholder="UUID do canal"
                    className="mt-1"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="step5-autoclose">
            <AccordionTrigger>5. Fechamento automático da conversa</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Fechamento automático da conversa</Label>
                  <Switch
                    checked={form.auto_close_enabled}
                    onCheckedChange={(v) => onFormChange('auto_close_enabled', v)}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Desligado: a conversa nunca é fechada automaticamente após o envio.
                </p>

                {form.auto_close_enabled && (
                  <div className="space-y-3 pl-1">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="auto_close_mode"
                        checked={form.auto_close_delay_minutes === 0}
                        onChange={() => onFormChange('auto_close_delay_minutes', 0)}
                      />
                      Fechar imediatamente após o envio
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="auto_close_mode"
                        checked={form.auto_close_delay_minutes > 0}
                        onChange={() =>
                          onFormChange(
                            'auto_close_delay_minutes',
                            form.auto_close_delay_minutes > 0 ? form.auto_close_delay_minutes : 30,
                          )
                        }
                      />
                      Fechar após um tempo
                    </label>

                    {form.auto_close_delay_minutes > 0 && (
                      <div className="flex items-center gap-2 pl-6">
                        <Input
                          type="number"
                          min={1}
                          className="w-24"
                          value={form.auto_close_delay_minutes}
                          onChange={(e) =>
                            onFormChange(
                              'auto_close_delay_minutes',
                              Math.max(1, Number(e.target.value) || 1),
                            )
                          }
                        />
                        <Select
                          value={form.auto_close_unit}
                          onValueChange={(v) => onFormChange('auto_close_unit', v as 'minutes' | 'hours')}
                        >
                          <SelectTrigger className="w-32 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minutes">minutos</SelectItem>
                            <SelectItem value="hours">horas</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-gray-500">após a confirmação de entrega (máx. 72h)</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="step6">
            <AccordionTrigger>6. Salvar configurações</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <p className="text-gray-700 text-sm">
                Clique em <strong>Salvar Configurações</strong> abaixo. Pronto, seu canal estará configurado.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={onSave}
                  disabled={saving || !form.account_name.trim() || (!isEdit && !form.account_token.trim())}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {saving ? 'Salvando...' : isEdit ? 'Salvar Alterações' : 'Adicionar Conexão'}
                </Button>
                {onCancel && (
                  <Button onClick={onCancel} variant="outline" disabled={saving}>
                    Cancelar
                  </Button>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="step7">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                7. Equipes da conexão
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-3">
              {!isEdit ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Salve a conexão primeiro para carregar as equipes do ÁtomosChat.
                </p>
              ) : (
                <>
                  <p className="text-gray-700 text-sm">
                    Carregue as equipes (departments) cadastradas no ÁtomosChat para esta conexão.
                    Elas ficam disponíveis em <strong>Atribuir a uma equipe</strong> na Central da Comunicação.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={loadAndSaveEquipes}
                      disabled={loadingTeams}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {loadingTeams ? 'Carregando...' : 'Carregar equipes'}
                    </Button>
                  </div>
                  {teams.length > 0 && (
                    <div className="overflow-x-auto mt-2">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-teal-200 text-left text-teal-800">
                            <th className="py-2 pr-3 font-semibold w-1/2">Nome da equipe</th>
                            <th className="py-2 pr-3 font-semibold">ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teams.map((t) => (
                            <tr key={t.team_id} className="border-b border-teal-100">
                              <td className="py-2 pr-3 font-medium text-gray-900">{t.team_name}</td>
                              <td className="py-2 pr-3 font-mono text-xs text-gray-700">{t.team_id}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!loadingTeams && teams.length === 0 && (
                    <p className="text-xs text-gray-600">
                      Nenhuma equipe carregada ainda. Clique no botão acima.
                    </p>
                  )}
                </>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="step8">
            <AccordionTrigger>8. Sincronizar templates</AccordionTrigger>
            <AccordionContent>
              <p className="text-gray-700 text-sm">
                Depois de salvar, use o botão <strong>Carregar Templates</strong> no card logo abaixo para sincronizar os templates.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
